import { useEffect, useRef, useState } from "react";
import type { LineStaff } from "./useWeek39LineStaff";

export function Week39CapacityStaffCell({ date, lineName, record, canEdit, loaded, unavailable, save, onPending }: {
  date: string; lineName: string; record?: LineStaff; canEdit: boolean; loaded: boolean; unavailable: boolean;
  save: (input: {date: string; lineName: string; count: number; expectedVersion: number}) => Promise<LineStaff>;
  onPending: (key: string, pending: boolean) => void;
}) {
  const value = record?.count.toString() ?? "";
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");
  const previous = useRef(value);
  const confirmedValue = useRef(value);
  const busy = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const key = `${date}|${lineName}`;
  const valid = draft.trim() !== "" && Number.isInteger(Number(draft)) && Number(draft) >= 0 && Number(draft) <= 999;
  const dirty = draft !== confirmedValue.current;
  useEffect(() => {
    setDraft(current => current === previous.current ? value : current);
    previous.current = value;
    confirmedValue.current = value;
  }, [value]);
  const commit = async () => {
    if (timer.current) clearTimeout(timer.current);
    if (draft === confirmedValue.current || !valid || busy.current || !canEdit || !loaded || unavailable || status === "error") return;
    const count = Number(draft);
    busy.current = true; setStatus("saving");
    try {
      await save({date,lineName,count,expectedVersion:record?.version ?? 0});
      confirmedValue.current = String(count);
      setDraft(current => current === draft ? String(count) : current);
      setStatus("saved");
      onPending(key,false);
    }
    catch (reason) {setStatus("error");setError(reason instanceof Error ? reason.message : "Kayıt başarısız.");}
    finally {busy.current = false;}
  };
  const latestCommit = useRef(commit); latestCommit.current = commit;
  useEffect(() => {
    onPending(key,dirty);
    if (dirty && valid && status === "idle") timer.current = setTimeout(() => void latestCommit.current(),600);
    return () => {if(timer.current) clearTimeout(timer.current);};
  }, [dirty,draft,key,onPending,valid,status]);
  useEffect(() => () => onPending(key,false), [key,onPending]);
  const id = `capacity-staff-${date}-${lineName}`;
  return <td className="pack39-capacity-staff-cell">
    {canEdit && loaded && !unavailable ? <div className="pack39-capacity-staff-input">
      <label className="pack39-sr-only" htmlFor={id}>{date} {lineName} çalışan kişi sayısı</label>
      <input id={id} type="number" min={0} max={999} step={1} inputMode="numeric" placeholder="—" value={draft}
        disabled={status === "saving"} aria-invalid={status === "error" || (dirty && !valid)} aria-describedby={`${id}-status`}
        onChange={event => {setDraft(event.target.value);onPending(key,event.target.value !== confirmedValue.current);setStatus("idle");setError("");}}
        onBlur={() => void latestCommit.current()} />
      <small id={`${id}-status`} role={status === "error" ? "alert" : "status"}>
        {error || (dirty && !valid ? "0–999 girin" : status === "saving" ? "Kaydediliyor…" : status === "saved" ? "Kaydedildi" : dirty ? "Bekliyor…" : "")}
      </small>
    </div> : <span>{!loaded ? "Yükleniyor" : unavailable ? "Veri alınamadı" : value || "—"}</span>}
  </td>;
}
